// src/components/automation/ExecutionHistory.tsx
import React, { useState, useEffect } from 'react';
import { automationService } from '../../services/automation.service';
import { AutomationExecution } from '../../types/automation.types';
import { format } from 'date-fns';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Zap,
  FileText
} from 'lucide-react';

export const ExecutionHistory: React.FC = () => {
  const [executions, setExecutions] = useState<AutomationExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = automationService.subscribeToExecutions((fetchedExecutions) => {
      setExecutions(fetchedExecutions);
      setLoading(false);
    }, 50);
    return () => unsubscribe();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'partial':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'failed':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'partial':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading execution history...</p>
      </div>
    );
  }

  if (executions.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">No execution history yet</h3>
        <p className="text-gray-500 mt-2">
          Automation rule executions will appear here once rules start running
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 mb-4">
        Showing the last {executions.length} rule executions
      </p>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="divide-y divide-gray-100">
          {executions.map((execution) => (
            <div key={execution.id} className="hover:bg-gray-50 transition-colors">
              <div
                className="px-5 py-4 cursor-pointer"
                onClick={() => setExpandedId(expandedId === execution.id ? null : execution.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {getStatusIcon(execution.status)}
                    <div>
                      <h4 className="font-medium text-gray-900">{execution.ruleName}</h4>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        <Zap className="h-3.5 w-3.5" />
                        <span className="capitalize">{execution.triggerEvent?.replace('_', ' ') || execution.triggerType}</span>
                        <span className="text-gray-300">•</span>
                        <span>{format(execution.executedAt.toDate(), 'MMM d, yyyy h:mm a')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(execution.status)}`}>
                      {execution.status === 'success' && 'Success'}
                      {execution.status === 'failed' && 'Failed'}
                      {execution.status === 'partial' && 'Partial'}
                    </span>
                    {expandedId === execution.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {expandedId === execution.id && (
                <div className="px-5 pb-4 pt-0">
                  <div className="bg-gray-50 rounded-lg p-4 ml-9">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Actions Executed</h5>
                    <div className="space-y-2">
                      {execution.actionsExecuted.map((action, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-white px-3 py-2 rounded-md border border-gray-100"
                        >
                          <div className="flex items-center gap-2">
                            {action.status === 'success' ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            <span className="text-sm text-gray-700 capitalize">
                              {action.actionType.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="text-xs">
                            {action.resultId && (
                              <span className="text-gray-400">
                                ID: {action.resultId.slice(0, 8)}...
                              </span>
                            )}
                            {action.error && (
                              <span className="text-red-500" title={action.error}>
                                Error
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {execution.triggerData && Object.keys(execution.triggerData).length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Trigger Data</h5>
                        <div className="text-xs text-gray-500 bg-white p-3 rounded-md border border-gray-100 font-mono overflow-x-auto">
                          {execution.triggerData.lead?.parentName && (
                            <div>Lead: {execution.triggerData.lead.parentName}</div>
                          )}
                          {execution.triggerData.appointment?.date && (
                            <div>Appointment: {execution.triggerData.appointment.date}</div>
                          )}
                          {execution.triggerData.projectId && (
                            <div>Project ID: {execution.triggerData.projectId}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExecutionHistory;
